import { Gender, GenderLabel } from "@/constants";
import { useGetRooms } from "@/hooks/query-hooks/useRoom";
import {
	Button,
	Form,
	Input,
	InputNumber,
	Modal,
	Segmented,
	Space,
} from "antd";
import { AiOutlineClose } from "react-icons/ai";

export interface RoomFormValues {
	name: string;
	maxiCount: number;
	gender: Gender;
}

interface RoomFormProps {
	open: boolean;
	onClose: () => void;
	onSubmit: (values: RoomFormValues) => void;
	initialValues?: Partial<RoomFormValues>;
	loading?: boolean;
}

export function RoomForm({
	open,
	onClose,
	onSubmit,
	initialValues,
	loading,
}: RoomFormProps) {
	const [form] = Form.useForm<RoomFormValues>();
	const { data: existingRooms = [] } = useGetRooms(open);

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			onSubmit(values);
		});
	};

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	return (
		<Modal
			title={
				<Space className="w-full justify-between">
					<h2 className="text-xl font-semibold text-primary-gradient font-poppins">
						Edit Room
					</h2>
					<Button
						type="text"
						className="rounded-full! bg-black/10! !size-8 aspect-square! flex items-center justify-center cursor-pointer"
						title="Close"
						onClick={handleCancel}
						icon={<AiOutlineClose />}
						disabled={loading}
					/>
				</Space>
			}
			open={open}
			onCancel={handleCancel}
			onOk={handleSubmit}
			okButtonProps={{
				className: "!px-6 bg-primary-gradient",
				shape: "round",
				size: "large",
				loading: loading,
			}}
			cancelButtonProps={{
				size: "large",
				shape: "round",
				disabled: loading,
			}}
			destroyOnClose
			okText="Update"
			classNames={{
				content: "[&>.ant-modal-close]:hover:bg-transparent! p-0!",
				body: "border-y border-gray-200 px-8! py-5!",
				header: "px-8! py-5!",
				footer: "px-8! py-5!",
			}}
			closeIcon={null}
			maskClosable={false}
		>
			<Form
				form={form}
				layout="vertical"
				initialValues={{
					gender: Gender.MONK,
					maxiCount: 1,
					...initialValues,
				}}
			>
				<Form.Item
					label="အခန်း နာမည်"
					name="name"
					rules={[
						{ required: true, message: "Please enter room name" },
						() => ({
							validator(_, value) {
								if (!value) return Promise.resolve();
								const normalizedValue = value
									.toLowerCase()
									.trim();
								const originalName = initialValues?.name
									?.toLowerCase()
									.trim();

								if (normalizedValue === originalName) {
									return Promise.resolve();
								}

								const isDbDuplicate = existingRooms.some(
									(r: Room) =>
										r.name.toLowerCase().trim() ===
										normalizedValue,
								);
								if (isDbDuplicate) {
									return Promise.reject(
										new Error(
											"Room name already exists in system",
										),
									);
								}

								return Promise.resolve();
							},
						}),
					]}
				>
					<Input placeholder="eg: အခန်း ၁" size="large" />
				</Form.Item>
				<Form.Item
					label="ယောဂီ အရေအတွက်"
					name="maxiCount"
					rules={[
						{ required: true, message: "Please enter max count" },
					]}
				>
					<InputNumber
						min={1}
						style={{ width: "100%" }}
						placeholder="Enter max count"
						size="large"
					/>
				</Form.Item>
				<Form.Item
					label="Gender"
					name="gender"
					rules={[
						{ required: true, message: "Please select gender" },
					]}
				>
					<Segmented
						options={Object.values(Gender)
							.filter((value) => value !== Gender.UNKNOWN)
							.map((value) => ({
								label: GenderLabel[value as Gender],
								value: value,
							}))}
						block
						size="large"
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
}
